import { supportButtonFootprintAtom } from "@/atoms/bottom-chrome";
import basicInfoAtom from "@/atoms/basic-info";
import profileAtom from "@/atoms/profile";
import { storefrontAppearanceAtom } from "@/atoms/storefront-appearance";
import { ButtonAnchor } from "@/components/ui/Button";
import { FloatingAction } from "@/components/ui/FloatingAction";
import { Icon, type IconSize } from "@/components/ui/Icon";
import {
  STOREFRONT_ROUNDED_PX,
  STOREFRONT_SUPPORT_BUTTON_SIZE,
  type StorefrontSizeToken,
} from "@/domain/storefront-appearance";
import { WhatsappLogo } from "@/icons";
import {
  buildWhatsAppLink,
  detectWhatsAppPlatform,
  expandSupportMessage,
  resolveWhatsAppLinkMode,
} from "@/lib/whatsapp-link";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useState } from "react";

const SUPPORT_ICON_SIZE: Record<StorefrontSizeToken, IconSize> = {
  compact: "sm",
  medium: "md",
  large: "lg",
};

/**
 * SUPPORT button: opens the merchant's WhatsApp without leaving the storefront.
 * Mounted on the `Router` (not per page). Hidden without `support.visible` OR
 * without a registered number. Rides above the bottom chrome via FloatingAction.
 */
export function FloatingSupportButton() {
  const { support } = useAtomValue(storefrontAppearanceAtom);
  const basicInfo = useAtomValue(basicInfoAtom);
  const profile = useAtomValue(profileAtom);
  const setFootprint = useSetAtom(supportButtonFootprintAtom);
  const [node, setNode] = useState<HTMLAnchorElement | null>(null);

  useEffect(() => {
    if (!node) {
      setFootprint(null);
      return;
    }

    const publish = () =>
      setFootprint({
        side: support.side,
        height: node.getBoundingClientRect().height,
      });

    const observer = new ResizeObserver(publish);
    observer.observe(node);
    publish();

    return () => {
      observer.disconnect();
      setFootprint(null);
    };
  }, [node, support.side, setFootprint]);

  const mode = resolveWhatsAppLinkMode(import.meta.env.VITE_WHATSAPP_LINK_MODE);
  const link = buildWhatsAppLink(
    {
      phone: basicInfo?.contactPhones?.whatsappNumber,
      message: expandSupportMessage(support.message, profile?.name),
    },
    detectWhatsAppPlatform(),
    mode,
  );

  if (!support.visible || !link) return null;

  return (
    <FloatingAction side={support.side}>
      <ButtonAnchor
        ref={setNode}
        href={link.href}
        target={link.target}
        rel={link.target ? "noopener noreferrer" : undefined}
        data-testid="storefront-support-button"
        data-link-mode={mode}
        aria-label="Tirar uma dúvida pelo WhatsApp"
        style={{ borderRadius: STOREFRONT_ROUNDED_PX[support.rounded] }}
        variant="solid"
        tone="secondary"
        size={STOREFRONT_SUPPORT_BUTTON_SIZE[support.size]}
        shape="square"
        elevation="raised"
        aspect={support.content === "icon" ? "square" : "auto"}
      >
        {support.content !== "label" && (
          <Icon as={WhatsappLogo} size={SUPPORT_ICON_SIZE[support.size]} weight="fill" />
        )}
        {support.content !== "icon" && "Dúvidas"}
      </ButtonAnchor>
    </FloatingAction>
  );
}
